import { useCallback, useState } from 'react';

import { loadCookie } from '@vimeo/core/libs/cookies';
import { Id, Optional } from '@vimeo/core/types/generic';
import { getLeadCaptureCookie } from '@vimeo/lead-capture/exports/helpers';
import { interactionToolsConfig } from '@vimeo/live-core/configs/InteractionToolsConfig';
import { useInterval } from '@vimeo/live-core/hooks/general/useInterval';

/**
 * Get registrant stored in cookies.
 * Handles logics for events with registration.
 */
function getRegistrantCookie(sessionId: Optional<Id>): Optional<string> {
  if (sessionId) {
    // Get new cookie or fallback to old one otherwise.
    return (
      getLeadCaptureCookie(String(sessionId)) ||
      // todo: Remove old one once new flow is registered.
      loadCookie(interactionToolsConfig.AUTHORIZATION.REGISTRANT_COOKIE(sessionId as string)) ||
      null
    );
  }

  return null;
}

/**
 * Polls registrant cookie and gets up-to-date status.
 *
 * @returns current registrant cookie from player
 */
export function useRegistrantLoginCookie(
  isPollingEnabled: boolean,
  sessionId: Optional<Id>,
): Optional<string> {
  const [registrantCookie, setRegistrantCookie] = useState<Optional<string>>(null);

  const onCheckNeeded = useCallback(() => {
    setRegistrantCookie(getRegistrantCookie(sessionId));
  }, [sessionId]);

  // If have correct context -> enabled + have uuid + still got no results
  useInterval(Boolean(isPollingEnabled && sessionId && !registrantCookie), onCheckNeeded, {
    checkCallable: true,
    initialize: true,
    period: 500,
  });

  return sessionId ? registrantCookie : null;
}
